import { memo, useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import {
  AlertCircle,
  CheckCircle2,
  FileText,
  Users,
  Target,
  FileQuestion,
  Globe,
  Info,
  ArrowLeft,
  X,
  Loader2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Switch } from "@/components/ui/switch";
import { CreditIcon } from "@/components/icons/CreditIcon";
import { DeliveryFormatSection } from "./goals/DeliveryFormatSection";
import { CreationFlowStickyActionBar } from "./CreationFlowStickyActionBar";
import Link from "@/routing/Link";
import { useRouter } from "@/routing/navigation";
import { ROUTES } from "@/routes/routes";
import type { ReviewStepProps } from "@/types/components/review-step";
import { audienceTierDisplayName } from "@/lib/billing/display-names";
import { ROOM_LANGUAGES } from "@/lib/constants/languages";
import { AnalyticsEvents, track } from "@/lib/services/analytics";
import { TRIAL_MAX_STUDIES } from "@/lib/billing/trial-limits";
import {
  billableUnitsForDesign,
  DEFAULT_MAX_RESPONDENTS,
  evaluateStudyDesign,
  studyCreditCost,
} from "@digital-twins/shared-types";
import { CREATION_CONSTANTS } from "../state/creationState";
import { useTranslation } from "react-i18next";
import { StudySizingSection } from "./StudySizingSection";
import { stimulusCutLabels } from "../utils/perGroupSizing";
import { testedMaterialsSummary } from "../utils/testedMaterials";
import { estimatedQuestionnaireMinutes } from "@digital-twins/shared-types";
import { getStudyTypeMeta } from "@/features/studies/studyTypeMeta";
import { AudienceReadinessNotice } from "@/components/audiences/AudienceReadinessNotice";

// Delivery-format toggles (deck/twin chat) are hidden until the feature is
// fully tested. Studies launch with the tested defaults (both enabled) while
// this is false. Flip to true to re-expose the controls.
const SHOW_DELIVERY_FORMAT_TOGGLES = false;

export const ReviewStep = memo(function ReviewStep({
  studyType,
  goalId,
  respondentLimits: respondentLimitsProp,
  hasAudience,
  audience,
  surveyJson,
  surveyQuestionCount,
  studyName,
  setStudyName,
  onCommitStudyName,
  numRespondents,
  setNumRespondents,
  design,
  contentLanguage,
  creditInfo,
  experimentCost,
  trialStudyLimitReached = false,
  delivery,
  studyMode,
  onStudyModeChange,
  onBack,
  onLaunch,
  isLaunching = false,
}: ReviewStepProps) {
  const { t, i18n } = useTranslation("app");
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const router = useRouter();

  useEffect(() => {
    if (trialStudyLimitReached) {
      track(AnalyticsEvents.TRIAL_LIMIT_REACHED, { limit: "studies" });
    }
  }, [trialStudyLimitReached]);

  const handleUpgrade = () => {
    track(AnalyticsEvents.TRIAL_LIMIT_UPGRADE_PLANS_CLICKED, { limit: "studies" });
    router.push("/settings/billing/plans");
  };

  const respondentLimits =
    respondentLimitsProp ??
    studyType.respondentLimits ?? {
      min: 30,
      max: DEFAULT_MAX_RESPONDENTS,
    };

  // A study type may omit either bound; the sizing inputs use the same defaults.
  const minRespondents = respondentLimits.min ?? 30;
  const maxRespondents = respondentLimits.max ?? DEFAULT_MAX_RESPONDENTS;

  const effectiveMax = studyMode === 'quick'
    ? Math.min(
        maxRespondents,
        CREATION_CONSTANTS.QUICK_MODE_MAX_RESPONDENTS,
      )
    : maxRespondents;

  useEffect(() => {
    if (numRespondents != null && numRespondents > effectiveMax) {
      setNumRespondents(effectiveMax);
    }
  }, [studyMode, effectiveMax, numRespondents, setNumRespondents]);

  const effectiveCost = studyMode === 'quick'
    ? Math.ceil(experimentCost / 2)
    : experimentCost;

  const billingDesign = design?.value ?? "comparative";
  const billingCutCount = design?.cutCount ?? 1;
  // Group names for the sizing grid, in the same cut order the engine uses.
  const cutLabels = useMemo(() => stimulusCutLabels(surveyJson), [surveyJson]);
  const testedMaterials = useMemo(
    () => testedMaterialsSummary(surveyJson),
    [surveyJson],
  );
  const designDecision =
    design && design.cutCount >= 2
      ? evaluateStudyDesign({
          requestedDesign: design.value,
          cutCount: design.cutCount,
          numRespondents: numRespondents ?? 0,
          personaCount: audience.personaCount ?? null,
        })
      : null;
  const designRefusal = designDecision?.refusal ?? null;
  // The evaluator writes for engineers. Restate the two a marketer can actually
  // hit in their own terms; anything else falls through verbatim.
  const designRefusalMessage =
    designRefusal == null
      ? null
      : designRefusal.code === "POOL_TOO_THIN"
        ? t("creation.poolTooThin", {
            twins: audience.personaCount ?? 0,
            needed: numRespondents ?? 0,
          })
        : designRefusal.code === "POOL_UNKNOWN"
          ? t("creation.poolUnknown")
          : designRefusal.message;
  const billableUnits =
    numRespondents != null
      ? billableUnitsForDesign(billingDesign, numRespondents, billingCutCount)
      : null;
  // A design that reuses the same twin across every cut (twin monadic) bills
  // one response per cut, so the billed count can run ahead of the
  // respondent count above it. Comparison and true monadic never differ here.
  const hasBillingMultiplier =
    billableUnits != null &&
    numRespondents != null &&
    billableUnits !== numRespondents;
  const totalCredits =
    numRespondents != null && billableUnits != null
      ? studyCreditCost({
          creditsPerRespondent: experimentCost,
          billableUnits,
          studyMode: studyMode === "quick" ? "quick" : "full",
        })
      : null;
  const creditsLoading = creditInfo.available < 0;
  const hasSufficientCredits =
    totalCredits !== null &&
    !creditsLoading &&
    creditInfo.available >= totalCredits;
  const allowOverage = creditInfo.allowOverage ?? false;
  const willUseOverage =
    totalCredits !== null &&
    !hasSufficientCredits &&
    allowOverage &&
    !creditsLoading;
  const overageAmount =
    willUseOverage && totalCredits !== null
      ? totalCredits - creditInfo.available
      : 0;
  const creditsOk = hasSufficientCredits || willUseOverage;

  const canLaunch =
    hasAudience &&
    studyName.trim().length > 0 &&
    numRespondents != null &&
    numRespondents >= minRespondents &&
    numRespondents <= effectiveMax &&
    creditsOk &&
    surveyQuestionCount > 0 &&
    !creditsLoading &&
    !trialStudyLimitReached &&
    designRefusal == null;

  const surveyEstMins = estimatedQuestionnaireMinutes(surveyQuestionCount);

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div className="flex-1 overflow-y-auto space-y-8 pb-7">
        {/* Header */}
        <div className="space-y-3">
          <h2 className="font-display text-4xl font-normal leading-[1.2] tracking-[-0.02em] text-foreground dark:text-white">
            {t("creation.review.readyTitle")}
          </h2>
          <p className="text-lg text-muted-foreground dark:text-[#BDBDBD] tracking-tight">
            {t("creation.review.readySubtitle")}
          </p>
        </div>

        {/* Main Content - Two Columns */}
        <div className="flex gap-4">
          {/* Left Column */}
          <div className="flex-1 min-w-0 space-y-3">
            {/* Study Summary Card */}
            <div className="bg-card dark:bg-card rounded-2xl p-7 space-y-5">
              <h3 className="text-lg font-medium text-foreground dark:text-white">
                {t("creation.review.summary")}
              </h3>

              <div className="flex items-stretch gap-5">
                {/* Type of study */}
                <SummaryColumn
                  icon={<FileText className="h-3 w-3" />}
                  label={t("creation.review.typeOfStudy")}
                  value={getStudyTypeMeta(goalId).label}
                />
                <VerticalDivider />
                {/* Audience */}
                <SummaryColumn
                  icon={<Users className="h-3 w-3" />}
                  label={t("creation.review.audience")}
                  value={audience.name}
                  subValue={audience.narrowing}
                />
                <VerticalDivider />
                {/* Testing: the study's tested assets; none, no column */}
                {testedMaterials ? (
                  <>
                    <SummaryColumn
                      icon={<Target className="h-3 w-3" />}
                      label={t("creation.review.testing")}
                      value={testedMaterials}
                    />
                    <VerticalDivider />
                  </>
                ) : null}
                {/* Survey */}
                <SummaryColumn
                  icon={<FileQuestion className="h-3 w-3" />}
                  label={t("creation.review.survey")}
                  value={t("creation.review.questions", { count: surveyQuestionCount })}
                  subValue={t("creation.review.mins", { count: surveyEstMins })}
                />
                <VerticalDivider />
                {/* Language — read-only; chosen in the questionnaire builder */}
                <SummaryColumn
                  icon={<Globe className="h-3 w-3" />}
                  label={t("creation.review.language")}
                  value={
                    ROOM_LANGUAGES.find((l) => l.code === contentLanguage)
                      ?.name ?? "English"
                  }
                />
              </div>
            </div>

            {/* Study Details Card */}
            <div className="bg-card dark:bg-card rounded-2xl p-7 space-y-5">
              <h3 className="text-lg font-medium text-foreground dark:text-white">
                {t("creation.review.details")}
              </h3>

              <div className="space-y-5">
                {/* Study Name */}
                <div className="rounded-xl">
                  <label className="text-sm text-foreground dark:text-white block mb-0">
                    {t("creation.review.studyName")}
                  </label>
                  <div className="border-b border-border py-2">
                    <input
                      type="text"
                      value={studyName}
                      onChange={(e) => setStudyName(e.target.value)}
                      // A rename is saved when the field is left or Enter
                      // is pressed; launch waits for that save.
                      onBlur={() => void onCommitStudyName()}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") e.currentTarget.blur();
                      }}
                      placeholder={t("creation.review.studyNamePlaceholder")}
                      disabled={isLaunching}
                      className="w-full bg-transparent text-xl text-foreground dark:text-white placeholder:text-muted-foreground/50 dark:placeholder:text-[#BDBDBD]/50 outline-none disabled:opacity-60"
                    />
                  </div>
                </div>

                {/* Sizing — one number per group, and what it buys */}
                <StudySizingSection
                  design={billingDesign}
                  cutCount={billingCutCount}
                  labels={cutLabels}
                  numRespondents={numRespondents}
                  setNumRespondents={setNumRespondents}
                  minRespondents={minRespondents}
                  maxRespondents={effectiveMax}
                  disabled={isLaunching}
                />

                {/* Shapes that cannot run at all still block the launch.
                    An under-target base is not one of them: it warns in the
                    sizing section instead. */}
                {designRefusal ? (
                  <p
                    className="text-sm text-destructive"
                    data-testid="study-design-refusal"
                  >
                    {designRefusalMessage}
                  </p>
                ) : null}
              </div>
            </div>

          </div>

          {/* Right Column - Cost Card */}
          <div className="w-[320px] flex-shrink-0">
            <div className="bg-card dark:bg-card rounded-2xl p-7 space-y-5 sticky top-4">
              <h3 className="text-lg font-medium text-foreground dark:text-white">
                {t("creation.review.cost")}
              </h3>

              <div className="space-y-2">
                <div className="rounded-xl flex justify-between items-end">
                  <span className="text-sm text-muted-foreground dark:text-[#BDBDBD]">
                    {t("creation.review.respondents")}
                  </span>
                  <span className="text-base font-semibold text-foreground dark:text-white">
                    {numRespondents ?? "—"}
                  </span>
                </div>
                {/* Only shown when the design bills more responses than
                    respondents (twin monadic reusing twins across cuts), so
                    the total below does not look like a math error. */}
                {hasBillingMultiplier && billableUnits != null && (
                  <div className="rounded-xl flex justify-between items-end">
                    <span className="text-sm text-muted-foreground dark:text-[#BDBDBD]">
                      {t("creation.review.billedAs")}
                    </span>
                    <span
                      className="text-base font-semibold text-foreground dark:text-white"
                      data-testid="review-billing-units"
                    >
                      {t("creation.review.billedAsResponses", { count: billableUnits })}
                    </span>
                  </div>
                )}
                <div className="rounded-xl flex justify-between items-end">
                  <span className="text-sm text-muted-foreground dark:text-[#BDBDBD]">
                    {t("creation.review.costPerRespondent")}
                  </span>
                  <span
                    className="text-base font-semibold text-foreground dark:text-white"
                    data-testid="review-cost-per-respondent"
                  >
                    {studyMode === 'quick' && (
                      <span className="line-through text-muted-foreground mr-1.5 font-normal">
                        {experimentCost}
                      </span>
                    )}
                    {t("creation.review.credits", { count: effectiveCost })}
                    {audience.tier && (
                      <span className="ml-1 text-xs font-normal text-muted-foreground">
                        ({audienceTierDisplayName(audience.tier)})
                      </span>
                    )}
                  </span>
                </div>
                <div className="h-px bg-border" />
                <div className="rounded-xl flex justify-between items-end">
                  <span className="text-sm font-medium text-foreground dark:text-white flex items-center gap-1.5">
                    <CreditIcon className="h-4 w-4" />
                    {t("creation.review.total")}
                  </span>
                  <span className="text-base font-semibold text-foreground dark:text-white">
                    {totalCredits === null
                      ? "—"
                      : t("creation.review.credits", { count: totalCredits })}
                  </span>
                </div>
              </div>

              {/* Data mode: no-deck quick run. Off by default (full study). */}
              <div
                className="flex items-center justify-between gap-3 pt-1"
                data-testid="study-mode-toggle"
              >
                <div className="flex flex-col gap-0.5">
                  <span
                    className={cn(
                      "text-sm",
                      studyMode === "quick"
                        ? "text-foreground"
                        : "text-muted-foreground",
                    )}
                  >
                    {t("creation.review.dataMode")}
                  </span>
                  <span
                    id="data-mode-hint"
                    className="text-xs text-muted-foreground"
                    data-testid="data-mode-hint"
                  >
                    {t("creation.review.dataModeHint", {
                      max: CREATION_CONSTANTS.QUICK_MODE_MAX_RESPONDENTS,
                    })}
                  </span>
                </div>
                <Switch
                  checked={studyMode === "quick"}
                  disabled={isLaunching}
                  onCheckedChange={(checked) =>
                    onStudyModeChange?.(checked ? "quick" : "full")
                  }
                  data-testid="data-mode-toggle"
                  aria-label={t("creation.review.dataMode")}
                  aria-describedby="data-mode-hint"
                />
              </div>

              {SHOW_DELIVERY_FORMAT_TOGGLES && delivery && (
                <DeliveryFormatSection
                  deckEnabled={delivery.deckEnabled}
                  twinChatEnabled={delivery.twinChatEnabled}
                  onDeckEnabledChange={delivery.onDeckEnabledChange}
                  onTwinChatEnabledChange={delivery.onTwinChatEnabledChange}
                />
              )}

              {/* Credits Status */}
              {creditsLoading ? (
                <div className="rounded-xl bg-muted dark:bg-border/30 p-3 flex items-center gap-3">
                  <Loader2 className="h-5 w-5 text-muted-foreground dark:text-[#BDBDBD] animate-spin flex-shrink-0" />
                  <span className="text-sm text-muted-foreground dark:text-[#BDBDBD]">
                    {t("billing.costCard.checking")}
                  </span>
                </div>
              ) : totalCredits === null ? (
                <div className="rounded-xl bg-muted/50 dark:bg-border/20 p-3 flex items-start gap-3">
                  <Info className="h-5 w-5 text-muted-foreground dark:text-[#BDBDBD] flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-muted-foreground dark:text-[#BDBDBD]">
                    {t("billing.costCard.enterRespondents")}
                  </p>
                </div>
              ) : hasSufficientCredits && !trialStudyLimitReached ? (
                <div className="rounded-xl bg-accent-highlight/10 dark:bg-accent-highlight/20 p-3 flex items-center gap-3">
                  <CheckCircle2 className="h-5 w-5 text-accent-highlight flex-shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-foreground dark:text-white">
                      {t("billing.costCard.enough")}
                    </p>
                    <p className="text-sm text-muted-foreground dark:text-[#BDBDBD]">
                      {t("billing.costCard.available", {
                        formatted: creditInfo.available.toLocaleString(i18n.language),
                      })}
                    </p>
                  </div>
                </div>
              ) : willUseOverage ? (
                <div className="flex items-start gap-2">
                  <Info className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-foreground dark:text-white">
                      {t("billing.costCard.studyOverage")}
                    </p>
                    <p className="text-xs text-muted-foreground dark:text-[#BDBDBD] mt-0.5">
                      {t("billing.costCard.overageBody", {
                        formatted: overageAmount.toLocaleString(i18n.language),
                      })}
                    </p>
                  </div>
                </div>
              ) : !trialStudyLimitReached ? (
                <div className="rounded-xl bg-destructive/10 border border-destructive/30 p-3 flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-destructive">
                      {t("billing.costCard.insufficient")}
                    </p>
                    <p className="text-xs text-destructive/90 mt-0.5">
                      {t("billing.costCard.needMore", {
                        formatted: (totalCredits - creditInfo.available).toLocaleString(
                          i18n.language,
                        ),
                      })}
                    </p>
                    <Link
                      href={ROUTES.settingsTab('billing')}
                      className="text-xs text-destructive underline mt-1 block"
                    >
                      {t("billing.costCard.manage")}
                    </Link>
                  </div>
                </div>
              ) : null}

              <AudienceReadinessNotice readiness={audience.readiness} />

              {/* Trial study limit notice */}
              {trialStudyLimitReached && (
                <div
                  className="rounded-xl border-2 border-[hsl(var(--cta))]/40 bg-background p-4 space-y-2.5"
                  data-testid="trial-study-limit-disclaimer"
                >
                  <div className="space-y-1">
                    <p className="text-[16px] font-semibold text-foreground dark:text-white flex items-start gap-2">
                      <AlertCircle className="h-4 w-4 text-[hsl(var(--cta))] flex-shrink-0 mt-[3px]" />
                      You&apos;ve reached your {TRIAL_MAX_STUDIES}-study trial limit
                    </p>
                    <p className="text-[14px] leading-relaxed text-muted-foreground dark:text-[#BDBDBD]">
                      Your draft study is saved — upgrade your plan to launch it.
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="cta"
                    onClick={handleUpgrade}
                    className="rounded-md px-3 text-[16px] w-full"
                    data-testid="trial-study-limit-upgrade-cta"
                  >
                    {t("creation.review.upgradePlan")}
                  </Button>
                </div>
              )}

              {!canLaunch && !creditsLoading && (
                <div className="text-xs text-destructive space-y-1">
                  {!hasAudience && <p>• Select an audience</p>}
                  {!studyName.trim() && <p>• Study name is required</p>}
                  {numRespondents === null && (
                    <p>• Enter number of respondents</p>
                  )}
                  {numRespondents != null &&
                    numRespondents < minRespondents && (
                      <p>• Need at least {minRespondents} respondents</p>
                    )}
                  {numRespondents != null &&
                    numRespondents > effectiveMax && (
                      <p>• Maximum {effectiveMax} respondents</p>
                    )}
                  {!creditsOk && numRespondents != null && !trialStudyLimitReached && (
                    <p>• Insufficient credits</p>
                  )}
                  {surveyQuestionCount === 0 && (
                    <p>• Survey has no questions</p>
                  )}
                  {designRefusal && (
                    <p data-testid="study-design-launch-block">
                      • {designRefusalMessage}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <CreationFlowStickyActionBar
        start={
          <Button
            type="button"
            variant="ghost"
            onClick={onBack}
            disabled={isLaunching}
            className="h-8 rounded-md px-3 text-[13px] font-medium text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="mr-1.5 h-3.5 w-3.5" />
            {t("creation.review.editQuestionnaire")}
          </Button>
        }
        center={
          <Button
            type="button"
            variant="cta"
            data-testid="study-launch-button"
            onClick={() => setShowConfirmDialog(true)}
            disabled={!canLaunch || isLaunching}
            className="h-10 rounded-md px-4 text-[16px] font-medium tracking-[-0.02em]"
          >
            {isLaunching ? (
              <>
                <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                {t("creation.review.launching")}
              </>
            ) : (
              t("creation.review.launch")
            )}
          </Button>
        }
      />

      {/* Launch Confirmation Dialog */}
      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent className="bg-background dark:bg-card border-border dark:border-[hsl(var(--card-inset))] max-w-[540px] rounded-2xl shadow-[0px_4px_19.6px_0px_rgba(0,0,0,0.08)] p-0 gap-0">
          <div className="flex items-center justify-end p-3">
            <button
              type="button"
              onClick={() => setShowConfirmDialog(false)}
              className="text-muted-foreground hover:text-foreground dark:text-white/60 dark:hover:text-white transition-colors"
              aria-label={t("creation.review.close")}
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="flex flex-col items-center gap-7 px-10 pb-10">
            <AlertDialogHeader className="text-center sm:text-center space-y-3 w-full">
              <AlertDialogTitle className="font-display text-[24px] font-normal text-foreground dark:text-white tracking-[-0.48px] leading-[1.2]">
                {t("creation.review.launchConfirmTitle")}
              </AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="flex flex-col items-center gap-1">
                  <p className="text-[16px] leading-[1.6] text-foreground dark:text-white">
                    {numRespondents != null && totalCredits != null
                      ? t("creation.review.launchConfirmCost", {
                          name: studyName || t("creation.review.thisStudy"),
                          respondents: numRespondents,
                          credits: totalCredits,
                        })
                      : t("creation.review.launchConfirmSimple", {
                          name: studyName || t("creation.review.thisStudy"),
                        })}
                  </p>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>

            <AlertDialogFooter className="flex-row justify-center sm:justify-center gap-4 sm:space-x-0">
              <AlertDialogAction
                onClick={() => {
                  setShowConfirmDialog(false);
                  onLaunch();
                }}
                disabled={isLaunching}
                className="h-10 rounded-[6px] px-4 text-[16px] font-medium bg-cta text-cta-foreground hover:bg-cta/90"
              >
                {isLaunching ? (
                  <>
                    <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                    {t("creation.review.launching")}
                  </>
                ) : (
                  t("creation.review.launchNow")
                )}
              </AlertDialogAction>
              <AlertDialogCancel
                disabled={isLaunching}
                className="mt-0 sm:mt-0 h-10 rounded-[6px] px-4 text-[16px] font-medium border-foreground dark:border-input-focus bg-transparent text-foreground dark:text-input-focus hover:bg-foreground/10 dark:hover:bg-input-focus/10 hover:text-foreground dark:hover:text-input-focus"
              >
                {t("creation.review.cancel")}
              </AlertDialogCancel>
            </AlertDialogFooter>
          </div>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
});

function SummaryColumn({
  icon,
  label,
  value,
  subValue,
  valuePrefix,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  subValue?: string;
  valuePrefix?: React.ReactNode;
}) {
  return (
    <div className="flex-1 min-w-0 space-y-1">
      <div className="flex items-center gap-1">
        <span className="inline-flex items-center justify-center w-[22px] h-[22px] rounded-full bg-primary/20 text-primary dark:bg-card-inset dark:text-primary flex-shrink-0">
          {icon}
        </span>
        <span className="text-sm text-muted-foreground dark:text-[#BDBDBD]">
          {label}
        </span>
      </div>
      <div className="relative">
        {valuePrefix && <span className="absolute">{valuePrefix}</span>}
        <p
          className={cn(
            "text-base font-semibold text-foreground dark:text-white truncate",
            valuePrefix && "pl-5"
          )}
        >
          {value}
        </p>
        {subValue && (
          <p className="text-base font-semibold text-foreground dark:text-white">
            {subValue}
          </p>
        )}
      </div>
    </div>
  );
}

function VerticalDivider() {
  return <div className="w-px self-stretch bg-border" />;
}
